/**
 * Web Feed entity schemas — the owned contract for the WEB_FEED page template,
 * the `WebFeed` content block, and the normalized item shape both render.
 *
 * The surface is READ-ONLY DISPLAY. A feed item is never a content record: it
 * carries no platform identity, is never targeted, pushed, or commented on, and
 * is re-derived from the publisher's feed on every fetch. Ingestion — turning
 * external posts into owned content — is a separate body of work.
 *
 * Two authored surfaces share one settings shape so they cannot drift on what
 * an image placement or a tap-through value means:
 *   - `WebFeedPageContentSchema` — the whole screen is one feed (WEB_FEED page).
 *   - `WebFeedPropsSchema` (in `./page`) — one feed inside a CONTENT page.
 *
 * ADDRESSING. Each configured feed carries an opaque `feedId` minted at
 * authoring time. A CONTENT page may hold several `WebFeed` blocks, so the page
 * slug alone cannot say which feed a read is for; the client sends slug +
 * `feedId` and the server walks the published content tree for the matching
 * node and reads ITS url. The id is deliberately NOT Puck's node id (not stable
 * across duplication and re-drag) and NOT an ordinal (breaks on reorder). It is
 * meaningless off its own page, which is what keeps the fetch surface closed:
 * no caller ever names the host the platform fetches.
 */

import { z } from 'zod'

import { isHttpsUrl } from './blog-post'

// ============================================
// Feed URL — https only
// ============================================

/**
 * A school-pasted feed URL. There is NO domain allowlist (settled product
 * decision: this is read-only display by an author who already has publishing
 * power in their own tenant), but the scheme is closed to https — matching the
 * `external` LinkTarget variant and the backend adapter's own pre-request
 * guard. The adapter additionally refuses private/loopback/link-local hosts at
 * fetch time; a URL that merely parses is not yet a URL we will fetch.
 */
export const WebFeedUrlSchema = z
  .string()
  .max(2048)
  .refine(isHttpsUrl, { message: 'URL must be an absolute https: URL' })

/**
 * The same url, as an AUTHOR-IN-PROGRESS value: a real https url, or the empty
 * string for a feed that has been placed but not yet addressed.
 *
 * The empty case is load-bearing, not lenience. A `WebFeed` block enters the
 * page the moment it is dragged, with no url yet, and the editor autosaves the
 * WHOLE page a second later. If the stored shape refused an empty url, that
 * autosave — and every one after it until a valid url was typed — would fail
 * schema narrowing and take the entire page's save down with it, including
 * every unrelated block on it. A configured-but-empty feed is the normal
 * intermediate state of authoring, so the contract has to hold it.
 *
 * An empty url reaches the read path as "no feed configured": the endpoint
 * returns an empty list without fetching anything, which is the same thing a
 * viewer sees for a feed that is down. Nothing renders, nothing breaks.
 */
export const WebFeedUrlDraftSchema = z.union([z.literal(''), WebFeedUrlSchema])

// ============================================
// Display settings — shared by both surfaces
// ============================================

/** Image above the text, or image beside it. Applied per feed, not per item. */
export const WEB_FEED_IMAGE_PLACEMENT_VALUES = ['top', 'side'] as const
export const WebFeedImagePlacementSchema = z.enum(WEB_FEED_IMAGE_PLACEMENT_VALUES)
export type WebFeedImagePlacement = z.infer<typeof WebFeedImagePlacementSchema>

/**
 * Where a tap lands.
 *   `detail` — an in-app screen rendering the feed-supplied body, always
 *              offering a handoff to the source page so it cannot dead-end.
 *   `source` — the item's link in the embedded browser, no intermediate screen.
 *
 * This is an author setting rather than one platform rule because feeds differ:
 * a feed shipping full article bodies reads well in-app, while the teaser-only
 * feeds this work was built against (bodies of ~400 characters) are a dead end
 * there.
 */
export const WEB_FEED_TAP_THROUGH_VALUES = ['detail', 'source'] as const
export const WebFeedTapThroughSchema = z.enum(WEB_FEED_TAP_THROUGH_VALUES)
export type WebFeedTapThrough = z.infer<typeof WebFeedTapThroughSchema>

export const WEB_FEED_IMAGE_PLACEMENT_DEFAULT: WebFeedImagePlacement = 'top'
export const WEB_FEED_TAP_THROUGH_DEFAULT: WebFeedTapThrough = 'detail'

/**
 * The two display settings, as a field bag both surfaces merge in flat. Kept as
 * raw fields (rather than a nested object) so the Puck block's props stay flat
 * for the editor while remaining literally the same schema the page content
 * uses.
 */
export const WebFeedSettingsShape = {
  imagePlacement: WebFeedImagePlacementSchema.default(WEB_FEED_IMAGE_PLACEMENT_DEFAULT),
  tapThrough: WebFeedTapThroughSchema.default(WEB_FEED_TAP_THROUGH_DEFAULT)
}

export const WebFeedSettingsSchema = z.object(WebFeedSettingsShape)
export type WebFeedSettings = z.infer<typeof WebFeedSettingsSchema>

// ============================================
// Feed id — the opaque authored token
// ============================================

/**
 * URL-safe opaque token minted by the editor when a feed is first configured.
 * Bounded and character-constrained because it rides in a query string and is
 * matched against stored nodes; it carries no meaning and is never parsed.
 */
export const WEB_FEED_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/
export const WebFeedIdSchema = z
  .string()
  .regex(WEB_FEED_ID_PATTERN, 'Feed id must be 8–64 URL-safe characters (A–Z, a–z, 0–9, - or _)')

/**
 * How a feed is ADDRESSED — the fields any surface needs to name one feed and
 * nothing about how it is displayed. Split out from `WebFeedConfigShape` so a
 * surface that renders a feed its own way (the ICAL_FEED page, whose renderer is
 * the calendar screen) composes the addressing without inheriting display
 * settings it has no notion of. Both surfaces then share one answer to "what is
 * a legal feed url here", which is the part that must never drift.
 *
 * `lastProbedFeedUrl` is written SERVER-SIDE by the write path after a
 * successful author-time reachability probe, and is what lets a later save that
 * changed only a display setting skip the outbound fetch. It is part of the
 * stored shape (not incidental metadata) because the page editor autosaves the
 * whole payload on a one-second debounce — without it, every keystroke-adjacent
 * autosave would re-hit the school's website.
 *
 * It is SERVER-OWNED: the write path reads the value already persisted on the
 * page and ignores whatever the request carried, so an author cannot skip their
 * own reachability check by sending a `lastProbedFeedUrl` that matches the url
 * they are setting.
 */
export const WebFeedAddressShape = {
  feedId: WebFeedIdSchema,
  feedUrl: WebFeedUrlDraftSchema,
  lastProbedFeedUrl: WebFeedUrlSchema.optional()
}

/** Everything a WEB_FEED surface carries: the addressing plus its display settings. */
export const WebFeedConfigShape = {
  ...WebFeedAddressShape,
  ...WebFeedSettingsShape
}

// ============================================
// WEB_FEED page template content
// ============================================

/**
 * WEB_FEED page content — the whole screen is one feed. Modeled on
 * `FeedPageContentSchema`: a small `.strip()` object of authored settings, not
 * a Puck payload. Unknown keys are dropped rather than persisted, with the
 * consequence recorded for page content generally — a field added here is
 * silently dropped until the backend ships a rebuilt shared-types package.
 */
export const WebFeedPageContentSchema = z.object(WebFeedConfigShape).strip()
export type WebFeedPageContent = z.infer<typeof WebFeedPageContentSchema>

// ============================================
// Normalized feed item
// ============================================

/**
 * ONE normalized item, the single shape every renderer sees. Atom 1.0 and RSS
 * 2.0 both land here, so no consumer depends on a parser type or on which
 * format the school's CMS happens to publish.
 *
 * Everything except `id` and `title` is optional because real feeds omit any of
 * it: the evidence feed carries no `<category>` at all, image presence is
 * unreliable across publishers, and `<author>` is common but not guaranteed.
 * A renderer must treat every optional field as genuinely absent rather than
 * rendering an empty region for it.
 *
 * `bodyHtml` is ALREADY SANITIZED when it reaches a consumer — the normalizer
 * sanitizes before caching, so the cached form is the safe form. The mobile
 * renderer's own deny-list still applies as defense in depth; feed bodies come
 * from an arbitrary third-party site with no allowlist, a weaker trust boundary
 * than the admin-authored content that convention was written for.
 */
export const FeedItemSchema = z.object({
  /** Stable-per-fetch id derived from the entry's `<id>`/`<guid>`, else its link. */
  id: z.string().min(1),
  title: z.string().min(1),
  /** The item's source page. Absent when the entry carries no usable link. */
  link: WebFeedUrlSchema.optional(),
  publishedAt: z.string().datetime().optional(),
  author: z.string().min(1).optional(),
  /** Sanitized body HTML. Often a short teaser rather than the full article. */
  bodyHtml: z.string().optional(),
  /** https-only image URL; absent puts the item on the text-row path. */
  imageUrl: WebFeedUrlSchema.optional()
})
export type FeedItem = z.infer<typeof FeedItemSchema>
